/**
 * The Coveo Headless Search sub-package exposes exposes the engine, controllers, actions, and utility functions to build a search experience.
 *
 * @example
 * ```typescript
 * import { buildSearchBox, buildSearchEngine, getSampleSearchEngineConfiguration } from '@coveo/headless';
 *
 * const engine = buildSearchEngine({
 *    configuration: getSampleSearchEngineConfiguration()
 * });
 *
 * const searchBox = buildSearchBox(engine);
 * ```
 * @module Search
 */
import * as HighlightUtils from './utils/highlight.js';
export { buildSearchEngine, getSampleSearchEngineConfiguration, } from './app/search-engine/search-engine.js';
export { buildController } from './controllers/controller/headless-controller.js';
export { buildRelevanceInspector } from './controllers/relevance-inspector/headless-relevance-inspector.js';
export { buildContext } from './controllers/context/headless-context.js';
export { buildDictionaryFieldContext } from './controllers/dictionary-field-context/headless-dictionary-field-context.js';
export { buildDidYouMean } from './controllers/did-you-mean/headless-did-you-mean.js';
export { buildCategoryFacet } from './controllers/facets/category-facet/headless-category-facet.js';
export { buildFacet } from './controllers/facets/facet/headless-facet.js';
export { buildDateRange, buildDateFacet, } from './controllers/facets/range-facet/date-facet/headless-date-facet.js';
export { buildNumericRange, buildNumericFacet, } from './controllers/facets/range-facet/numeric-facet/headless-numeric-facet.js';
export { buildNumericFilter } from './controllers/facets/range-facet/numeric-facet/headless-numeric-filter.js';
export { buildDateFilter } from './controllers/facets/range-facet/date-facet/headless-date-filter.js';
export { buildHistoryManager } from './controllers/history-manager/headless-history-manager.js';
export { buildPager } from './controllers/pager/headless-pager.js';
export { buildQueryError } from './controllers/query-error/headless-query-error.js';
export { buildQuerySummary } from './controllers/query-summary/headless-query-summary.js';
export { buildResultList } from './controllers/result-list/headless-result-list.js';
export { buildInteractiveResult } from './controllers/result-list/headless-interactive-result.js';
export { buildInteractiveInstantResult } from './controllers/instant-results/headless-interactive-instant-result.js';
export { buildResultsPerPage } from './controllers/results-per-page/headless-results-per-page.js';
export { buildSearchBox } from './controllers/search-box/headless-search-box.js';
export { buildInstantResults } from './controllers/instant-results/instant-results.js';
export { buildSort } from './controllers/sort/headless-sort.js';
export { buildStaticFilterValue, buildStaticFilter, } from './controllers/static-filter/headless-static-filter.js';
export { buildTab } from './controllers/tab/headless-tab.js';
export { buildTabManager } from './controllers/tab-manager/headless-tab-manager.js';
export { buildFacetManager } from './controllers/facet-manager/headless-facet-manager.js';
export { buildBreadcrumbManager } from './controllers/breadcrumb-manager/headless-breadcrumb-manager.js';
export { buildStandaloneSearchBox } from './controllers/standalone-search-box/headless-standalone-search-box.js';
export { buildSearchParameterManager } from './controllers/search-parameter-manager/headless-search-parameter-manager.js';
export { buildUrlManager } from './controllers/url-manager/headless-url-manager.js';
export { buildSearchStatus } from './controllers/search-status/headless-search-status.js';
export { buildQuickview } from './controllers/quickview/headless-quickview.js';
export { buildFoldedResultList } from './controllers/folded-result-list/headless-folded-result-list.js';
export { buildRedirectionTrigger } from './controllers/triggers/headless-redirection-trigger.js';
export { buildQueryTrigger } from './controllers/triggers/headless-query-trigger.js';
export { buildExecuteTrigger } from './controllers/triggers/headless-execute-trigger.js';
export { buildNotifyTrigger } from './controllers/triggers/headless-notify-trigger.js';
export { buildSmartSnippet } from './controllers/smart-snippet/headless-smart-snippet.js';
export { buildSmartSnippetQuestionsList } from './controllers/smart-snippet-questions-list/headless-smart-snippet-questions-list.js';
export { buildRecentQueriesList } from './controllers/recent-queries-list/headless-recent-queries-list.js';
export { buildRecentResultsList } from './controllers/recent-results-list/headless-recent-results-list.js';
export { buildInteractiveRecentResult } from './controllers/recent-results-list/headless-interactive-recent-result.js';
export { buildCoreFacetConditionsManager as buildFacetConditionsManager } from './controllers/core/facets/facet-conditions-manager/headless-facet-conditions-manager.js';
export { buildFieldSuggestions } from './controllers/field-suggestions/facet/headless-field-suggestions.js';
export { buildCategoryFieldSuggestions } from './controllers/field-suggestions/category-facet/headless-category-field-suggestions.js';
export { buildAutomaticFacetGenerator } from './controllers/facets/automatic-facet-generator/headless-automatic-facet-generator.js';
export { buildGeneratedAnswer } from './controllers/generated-answer/headless-generated-answer.js';
export { buildInteractiveCitation } from './controllers/generated-answer/headless-interactive-citation.js';
//#endregion
//#endregion
// Selectors
export { baseFacetResponseSelector, facetRequestSelector, facetResponseSelector, facetResponseSelectedValuesSelector, } from './features/facets/facet-set/facet-set-selectors.js';
export { currentPageSelector, maxPageSelector, currentPagesSelector, } from './features/pagination/pagination-selectors.js';
//#region Grouped actions
export { ResultTemplatesHelpers } from './features/result-templates/result-templates-helpers.js';
export * from './features/advanced-search-queries/advanced-search-queries-actions-loader.js';
export * from './features/facets/category-facet-set/category-facet-set-actions-loader.js';
export * from './features/facets/facet-set/facet-set-actions-loader.js';
export * from './features/configuration/configuration-actions-loader.js';
export * from './features/configuration/search-configuration-actions-loader.js';
export * from './features/context/context-actions-loader.js';
export * from './features/dictionary-field-context/dictionary-field-context-actions-loader.js';
export * from './features/debug/debug-actions-loader.js';
export * from './features/facets/range-facets/date-facet-set/date-facet-actions-loader.js';
export * from './features/facet-options/facet-options-actions-loader.js';
export * from './features/did-you-mean/did-you-mean-actions-loader.js';
export * from './features/fields/fields-actions-loader.js';
export * from './features/history/history-actions-loader.js';
export * from './features/facets/range-facets/numeric-facet-set/numeric-facet-actions-loader.js';
export * from './features/folding/folding-actions-loader.js';
export * from './features/pagination/pagination-actions-loader.js';
export * from './features/pipeline/pipeline-actions-loader.js';
export * from './features/query/query-actions-loader.js';
export * from './features/query-set/query-set-actions-loader.js';
export * from './features/instant-results/instant-results-actions-loader.js';
export * from './features/query-suggest/query-suggest-actions-loader.js';
export * from './features/search/search-actions-loader.js';
export * from './features/search-hub/search-hub-actions-loader.js';
export * from './features/sort-criteria/sort-criteria-actions-loader.js';
export * from './features/standalone-search-box-set/standalone-search-box-set-actions-loader.js';
export * from './features/static-filter-set/static-filter-set-actions-loader.js';
export * from './features/tab-set/tab-set-actions-loader.js';
export * from './features/question-answering/question-answering-actions-loader.js';
export * from './features/breadcrumb/breadcrumb-actions-loader.js';
export * from './features/recent-queries/recent-queries-actions-loader.js';
export * from './features/recent-results/recent-results-actions-loader.js';
export * from './features/excerpt-length/excerpt-length-actions-loader.js';
export * from './features/result-preview/result-preview-actions-loader.js';
export * from './features/generated-answer/generated-answer-actions-loader.js';
export { buildResultTemplatesManager } from './features/result-templates/result-templates-manager.js';
//#endregion
// Analytics Actions
export * from './features/analytics/search-analytics-actions-loader.js';
export * from './features/analytics/click-analytics-actions-loader.js';
export * from './features/analytics/generic-analytics-actions-loader.js';
export * from './features/actions-history/ipx-actions-history-actions-loader.js';
// Types & Helpers
export { getOrganizationEndpoint, getSearchApiBaseUrl, getAnalyticsNextApiBaseUrl, } from './api/platform-client.js';
export { API_DATE_FORMAT } from './api/search/date/date-format.js';
export { HighlightUtils };
export { SortBy, SortOrder, buildDateSortCriterion, buildCriterionExpression, buildFieldSortCriterion, buildNoSortCriterion, buildQueryRankingExpressionSortCriterion, buildRelevanceSortCriterion, } from './features/sort-criteria/criteria.js';
export { parseCriterionExpression } from './features/sort-criteria/criteria-parser.js';
export { MinimumFieldsToInclude, DefaultFieldsToInclude, EcommerceDefaultFieldsToInclude, } from './features/fields/fields-state.js';
export { buildSearchParameterSerializer } from './features/search-parameters/search-parameter-serializer.js';
export { VERSION } from './utils/version.js';
export { deserializeRelativeDate, validateRelativeDate, } from './api/search/date/relative-date.js';
export * from './utils/query-expression/query-expression.js';
